import { forwardRef, type ButtonHTMLAttributes, type MouseEventHandler, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "../../lib/utils";

type Variant = "primary" | "secondary" | "ghost";
type Size = "sm" | "md" | "lg";

const variantClass: Record<Variant, string> = {
  primary:
    "bg-button text-on-button shadow-[0_0_12px_rgb(2_132_199/0.28)] hover:brightness-110",
  secondary:
    "border border-button/70 bg-transparent text-button hover:bg-button/10",
  ghost:
    "bg-transparent text-primary hover:bg-ink/5",
};

const sizeClass: Record<Size, string> = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-4 py-2.5 text-sm",
  lg: "px-6 py-3 text-base",
};

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> {
  variant?: Variant;
  size?: Size;
  href?: string;
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLElement>;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", size = "md", href, className, children, type = "button", onClick, ...props },
  ref,
) {
  const cls = cn(
    "inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition duration-200",
    "disabled:cursor-not-allowed disabled:opacity-60",
    variantClass[variant],
    sizeClass[size],
    className,
  );

  if (href) {
    const external = href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("tel:");
    if (href.startsWith("/")) {
      return (
        <Link to={href} className={cls} onClick={onClick}>
          {children}
        </Link>
      );
    }
    return (
      <a
        href={href}
        className={cls}
        rel={external && href.startsWith("http") ? "noreferrer" : undefined}
        target={href.startsWith("http") ? "_blank" : undefined}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  return (
    <button ref={ref} type={type} className={cls} onClick={onClick} {...props}>
      {children}
    </button>
  );
});
